import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import {
  cloneData, cloneDataOrNull, isSameData,
} from '../../dto/data-model-utils.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../../dto/data-model.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { LocalizedString } from '../localized-string.js';
import {
  MarketingVendorSettings,
} from './marketing-vendor-settings.js';

export class BusinessLevelMarketingSettings extends DataModel {
  static readonly TYPE_ID: string = 'd3b8a0ffda80417281c9bf8b7726de02';

  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly businessRef: BusinessModuleRef,
    public readonly isSubscribingEnabled: boolean,
    public readonly isSubscribingRequired: boolean,
    public readonly vendorSettings: MarketingVendorSettings | null,
    public readonly consentText: LocalizedString | null,
  ) {
    super();
  }

  get $typeId(): string {
    return BusinessLevelMarketingSettings.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, BusinessLevelMarketingSettings.SCHEMA_VERSION);
  }

  static create(args: {
    businessRef: BusinessModuleRef,
    isSubscribingEnabled: boolean,
    isSubscribingRequired: boolean,
    vendorSettings: MarketingVendorSettings | null,
    consentText: LocalizedString | null,
  }): BusinessLevelMarketingSettings {
    return new BusinessLevelMarketingSettings(
        args.businessRef,
        args.isSubscribingEnabled,
        args.isSubscribingRequired,
        args.vendorSettings,
        args.consentText,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): BusinessLevelMarketingSettings {
    return BusinessLevelMarketingSettings.create({
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      isSubscribingEnabled: MapUtils.getBoolean(map, 'isSubscribingEnabled'),
      isSubscribingRequired: MapUtils.getBoolean(map, 'isSubscribingRequired'),
      vendorSettings: context.fromDataOrNull(MapUtils.getRecordOrNull(map, 'vendorSettings')),
      consentText: context.fromDataOrNull(MapUtils.getRecordOrNull(map, 'consentText')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      businessRef: this.businessRef.encode(),
      isSubscribingEnabled: this.isSubscribingEnabled,
      isSubscribingRequired: this.isSubscribingRequired,
      vendorSettings: context.toDataOrNull(this.vendorSettings),
      consentText: context.toDataOrNull(this.consentText),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    businessRef = cloneData(this.businessRef),
    isSubscribingEnabled = this.isSubscribingEnabled,
    isSubscribingRequired = this.isSubscribingRequired,
    vendorSettings = cloneDataOrNull(this.vendorSettings),
    consentText = cloneDataOrNull(this.consentText),
  }: {
    businessRef?: BusinessModuleRef,
    isSubscribingEnabled?: boolean,
    isSubscribingRequired?: boolean,
    vendorSettings?: MarketingVendorSettings | null,
    consentText?: LocalizedString | null,
  } = {}): this {
    return BusinessLevelMarketingSettings.create({
      businessRef,
      isSubscribingEnabled,
      isSubscribingRequired,
      vendorSettings,
      consentText,
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.businessRef, this.businessRef) &&
      other.isSubscribingEnabled === this.isSubscribingEnabled &&
      other.isSubscribingRequired === this.isSubscribingRequired &&
      isSameData(other.vendorSettings, this.vendorSettings) &&
      isSameData(other.consentText, this.consentText);
  }
}

export class BusinessLevelMarketingSettingsBuilder extends DataModelBuilder<BusinessLevelMarketingSettings> {
  build(context: SerializationContext, map: SerializedData): BusinessLevelMarketingSettings {
    return BusinessLevelMarketingSettings.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(BusinessLevelMarketingSettings.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(BusinessLevelMarketingSettings.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /** Version 2:
     * - Add 'isSubscribingRequired' field of type {@link boolean}.
     */
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'isSubscribingRequired', false);
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
